const {brandColors} = require('./Colors/brandColors')

// Formats the brand colors so they can be used in the Tailwind config to create classes
const formattedColorsForTailwindConfig = Object.assign(
	{},
	...Object.keys(brandColors)
		.map((color) => {
			if (brandColors[color].backgroundColor !== '') {
				return {
					[`${color}`]: {
						...(brandColors[color].lightBackgroundColor !== '' &&
							brandColors[color].lightBackgroundColor !==
								'default' && {
								light: brandColors[color].lightBackgroundColor,
							}),
						...(brandColors[color].accentColor !== '' &&
							brandColors[color].accentColor !== 'default' && {
								accent: brandColors[color].accentColor,
							}),
						DEFAULT: brandColors[color].backgroundColor,
					},
				}
			}
			return undefined
		})
		.filter((item) => item)
)

// These classes are mainly used in the portfolio repo https://github.com/elanco/commercial-web-portfolioPet
// So when Tailwind looks in the files in this repo it won't see any of these color classes in use so they won't be included in the build
// This makes sure all of the required brand color classes are safelisted so they're included in the production Tailwind CSS file

const colorsTailwindClassesForSafelist = []

Object.keys(brandColors).forEach((color) => {
	if (brandColors[color].backgroundColor !== '') {
		colorsTailwindClassesForSafelist.push(`bg-${color}`)
		colorsTailwindClassesForSafelist.push(`!bg-${color}`)
		colorsTailwindClassesForSafelist.push(`border-${color}`)
		colorsTailwindClassesForSafelist.push(`!border-${color}`)
		colorsTailwindClassesForSafelist.push(`hover:bg-${color}/90`)
		colorsTailwindClassesForSafelist.push(`focus:bg-${color}/90`)
		colorsTailwindClassesForSafelist.push(`active:bg-${color}/90`)
		colorsTailwindClassesForSafelist.push(`[&>a]:!text-${color}`)
		colorsTailwindClassesForSafelist.push(`[&>a]:after:!bg-${color}`)
		colorsTailwindClassesForSafelist.push(`[&>a]:after:hover:!bg-${color}`)
		colorsTailwindClassesForSafelist.push(`[&>a]:hover:!text-${color}`)
	}
	if (
		brandColors[color].lightBackgroundColor !== '' &&
		brandColors[color].lightBackgroundColor !== 'default'
	) {
		colorsTailwindClassesForSafelist.push(`!bg-${color}-light`)
	}
	if (
		brandColors[color]?.accentColor !== '' &&
		brandColors[color]?.accentColor !== 'default'
	) {
		colorsTailwindClassesForSafelist.push(`hover:bg-${color}-accent`)
		colorsTailwindClassesForSafelist.push(`hover:border-${color}-accent`)
		colorsTailwindClassesForSafelist.push(`hover:text-${color}-accent`)
		colorsTailwindClassesForSafelist.push(`bg-${color}-accent`)
		colorsTailwindClassesForSafelist.push(`border-${color}-accent`)
		colorsTailwindClassesForSafelist.push(`text-${color}-accent`)
		colorsTailwindClassesForSafelist.push(`hover:!bg-${color}-accent`)
		colorsTailwindClassesForSafelist.push(`hover:!border-${color}-accent`)
		colorsTailwindClassesForSafelist.push(`hover:!text-${color}-accent`)
		colorsTailwindClassesForSafelist.push(`!bg-${color}-accent`)
		colorsTailwindClassesForSafelist.push(`!border-${color}-accent`)
		colorsTailwindClassesForSafelist.push(`!text-${color}-accent`)
	}
})

const generateColor =
	(colorName) =>
	({opacityVariable, opacityValue}) => {
		if (opacityValue !== undefined) {
			return `rgba(var(--color-${colorName}), ${opacityValue})`
		}
		if (opacityVariable !== undefined) {
			return `rgba(var(--color-${colorName}), var(${opacityVariable}, 1))`
		}
		return `rgb(var(--color-${colorName}))`
	}

module.exports = {
	darkMode: 'class',
	content: [
		'./src/components/**/*.{jsx,tsx}',
		'./src/modules/**/*.{jsx,tsx}',
		'./src/globals/**/*.{jsx,tsx}',
		'./src/globals/**/*.js',
		'./src/stories/**/*.{jsx,tsx}',
		'./src/css/nopurge.txt',
	],
	safelist: [
		...colorsTailwindClassesForSafelist,
		'hover:!text-white',
		'hover:!text-black',
		'!text-white',
		'!text-black',
	],
	theme: {
		colors: {
			inherit: 'inherit',
			current: 'currentColor',
			transparent: 'rgba(0, 0, 0, 0)',
			white: {
				DEFAULT: '#FFF',
				transparent: 'rgba(255,255,255,0.95)',
			},
			black: '#000',
			gray: {
				100: '#F5F5F5',
				200: '#EDEDED',
				300: '#E0E0E0',
				400: '#D3D3D3',
				500: '#BCBABA',
				600: '#707070',
				700: '#D3D3D3',
			},
			purple: {
				400: '#b51a8a',
			},
			orange: {
				400: '#f79727',
				500: '#ee7d00',
				600: '#ff9900',
			},
			green: {
				400: '#6cb33e',
				500: '#024526',
				600: '#1F8815',
			},
			blue: {
				100: '#EBF7FF',
				300: '#01394E',
				400: '#007fb1',
				500: '#0072CE',
				700: '#80929E',
				800: '#4D6678',
				900: '#02253E',
				1000: '#0072CE',
				transparent: 'rgba(2, 37, 62, 0.95)',
			},
			midnight: '#033357',
			red: {
				300: '#FAE7E5',
				500: '#EA0101',
				600: '#BE0202',
			},
			brown: {
				400: '#794400',
			},
			primary: {
				DEFAULT: '#0072CE',
				dark: '#04538D',
				700: '#80929E',
			},
			secondary: {
				DEFAULT: '#00AB9C',
			},

			// Arbitrary colors
			'elanco-beige': {DEFAULT: '#D5C4AF'},
			'elanco-blue': {DEFAULT: '#0072CE', a: '#000'},
			'elanco-grey': {DEFAULT: '#7F7F7F'},
			'light-blue': {DEFAULT: '#D9EAF8'},
			'mid-beige': {DEFAULT: '#EEE8DF'},
			'midnight-blue': {DEFAULT: '#001149'},
			'salmonella_360':{DEFAULT: '#EE7D00'},
			'zenrelia_primary' : {DEFAULT: '#215447'},
			'zenrelia_secondary' : {DEFAULT: '#91CC12'},
			'zenrelia_contrast' : {DEFAULT: '#A68CFA'},
			'zenrelia_accent' : {DEFAULT: '#c0d29a'},
			'zenrelia_sku1' : {DEFAULT: '#618582'},
			'zenrelia_sku2' : {DEFAULT: '#8752C7'},
			'zenrelia_sku3' : {DEFAULT: '#00A99E'},
			'zenrelia_sku4' : {DEFAULT: '#C6B5FF'},
			'claro_neptra' : {DEFAULT: '#522873'},
			'advocate_orange' : {DEFAULT: '#E57200'},
			'gallipro_fit_yellow': {DEFAULT: '#FBBA00'},
			'gallipro_fit_orange': {DEFAULT: '#EF7900'},
			'gallipro_fit_green': {DEFAULT: '#9EB184'},
			'gallipro_fit_darkblue': {DEFAULT: '#142C46'},
			'neptra_green': {DEFAULT: '#c7d530'},
			'seresto':{DEFAULT: '#00597c'},
			'clik_light_blue': {DEFAULT: '#0C78BD'},
			'clik_dark_blue': {DEFAULT: '#1F3560'},
			'clik_yellow': {DEFAULT: '#DB9300'},
			'tracer': {DEFAULT: '#ed037c'},
			'bovaer': {DEFAULT: '#027d50'},
			'pradalex': {DEFAULT: '#26d07c'},
			'avipro_grey': {DEFAULT: '#929292'},
			'avipro_red': {DEFAULT: '#d51317'},
			'aviblue_light_blue': {DEFAULT: '#41c0f0'},
			'aviblue_mid_blue': {DEFAULT: '#0078BF'},
			'aviblue_dark_blue': {DEFAULT: '#073F65'},
			'coliprotec_teal': {DEFAULT: '#0081a3'},
			'flubenvet': {DEFAULT: '#788e1d'},
			'hemicell_red': {DEFAULT: '#b32b2e'},
			'htsi': {DEFAULT: '#004268'},
			'maxiban_': {DEFAULT: '#d31245'},
			'galliprant_yellow': {DEFAULT: '#ffc600'},
			'galliprant_red': {DEFAULT: '#d11947'},
			'bexacat_dark_grey': {DEFAULT: '#262626'},
			'varenzin_blue': {DEFAULT: '#0056b8'},
			'varenzin_purple': {DEFAULT: '#6a488e'},
			'nuplura_ph_plus_5_primary': {DEFAULT: '#a1d060'},
			'nuplura_ph_plus_5_secondary': {DEFAULT: '#8b8c90'},
			'onsior': {DEFAULT: '#00a9ce'},
			'onsior_cool_gray_10': {DEFAULT: '#63666a'},
			'onsior_tangerine': {DEFAULT: '#ffb81c'},
			'onsior_dark_gray': {DEFAULT: '#2c2a29'},
			'onsior_cool_gray_6c': {DEFAULT: '#a7a8aa'},
			'onsior_dark_red': {DEFAULT: '#9d2235'},
			'onsior_vivid_orange': {DEFAULT: '#ff671f'},
			'onsior_dark_violet': {DEFAULT: '#3c1053'},
			'onsior_lime_green': {DEFAULT: '#00b140'},
			'onsior_dark_blue': {DEFAULT: '#003594'},
			'onsior_rhodamine_red': {DEFAULT: '#e10098'},
			'onsior_advantix_light': {DEFAULT: '#f9e5eb'},
			'agita_10wg': {DEFAULT: '#7ea0c4'},
			'baseline': {DEFAULT: '#db3168'},
			'baytril_100': {DEFAULT: '#008f87'},
			'evosure': {DEFAULT: '#e56606'},
			'feedaid': {DEFAULT: '#729786'},
			'full_value_pork_primary_blue': {DEFAULT: '#004267'},
			'full_value_pork_secondary_blue': {DEFAULT: '#30a9d7'},
			'lipinate': {DEFAULT: '#464c3e'},
			'pili_shield_plus_c': {DEFAULT: '#7f2629'},
			'proteck': {DEFAULT: '#cc9000'},
			'rhini_shield': {DEFAULT: '#009383'},
			'swine_awake': {DEFAULT: '#7b2379'},
			'tempo_one_percent_dust': {DEFAULT: '#75bc43'},
			'light_gray':{DEFAULT: '#f2f2f2'},
			'credelio_quattro_guard_dog':{DEFAULT: '#0F3320'},
			'credelio_quattro_hunter_green':{DEFAULT: '#00643C'},
			'credelio_quattro_grass_green':{DEFAULT: '#009548'},
			'credelio_quattro_tennis_ball_green':{DEFAULT: '#80BB4F'},
			'credelio_quattro_park_green':{DEFAULT: '#94CE95'},
			'credelio_quattro_milk_bone':{DEFAULT: '#EDECDE'},
			'credelio_quattro_golden':{DEFAULT: '#F68A33'},
			'credelio_quattro_frisbee':{DEFAULT: '#EC008C'},
			'credelio_quattro_sunny_day':{DEFAULT: '#EAAC18'},
			'credelio_quattro_sky_blue':{DEFAULT: '#0099D8'},
			'pet_protect_red': {DEFAULT: '#F9423A'},
			'pet_protect_green': {DEFAULT: '#13863D'},
			'pet_protect_blue': {DEFAULT: '#49B2E6'},
			'clean_up_2': {DEFAULT: '#92C050'},
			'cydectin': {DEFAULT: '#5C4586'},
			'micotil': {DEFAULT: '#13B5EA'},
			'scour_bos': {DEFAULT: '#6A2875'},
			'vira_shield': {DEFAULT: '#E05A28'},
			'pet_protect_skin': {DEFAULT: '#25B0E7'},
			'pet_protect_calm': {DEFAULT: '#C05694'},
			'pet_protect_allergy': {DEFAULT: '#70B925'},
			'civ': {DEFAULT: '#CFB62E'},
			'elmaro_navy_blue': {DEFAULT: '#00457f'},
			'elmaro_pink': {DEFAULT: '#eb7bae'},
			'elmaro_yellow': {DEFAULT: '#fbba00'},
			pet: {DEFAULT: '#2B9E3B'},
			vet: {DEFAULT: '#3818A2'},
			'default-light-background': {DEFAULT: '#d5c4af'},

			// Brand colors
			'elanco-green': {DEFAULT: '#0EC3A4'},
			'pet-green-light': {DEFAULT: '#CAE3C8'},
			'pet-green': {DEFAULT: '#1F8815'},
			'purple-light': {DEFAULT: '#EAE6F5'},
			'vet-purple': {DEFAULT: '#3818A2'},
			...formattedColorsForTailwindConfig,

			// Category colors
			'no_style': {DEFAULT: '#0072CE'},
			behaviour: {DEFAULT: '#d43900'},
			'new_pets': {DEFAULT: '#bd2ca2'},
			lifestyle: {DEFAULT: '#b50000'},
			'health_and_care': {DEFAULT: '#138187'},
			parasites: {DEFAULT: '#3a76bb'},

			// Theme colors
			theme: {
				main: generateColor('theme'),
				highlight: generateColor('theme-highlight'),
			},
		},

		boxShadow: {
			primary: '0 3px 6px 0 rgba(0, 0, 0, .16)',
			secondary: '0 2px 3px 0 rgba(0, 0, 0, .22)',
			tertiary: '0 4px 12px 0 rgba(0, 0, 0, .10)',
			around: '0px 0px 13px 0px rgba(0,0,0,0.2)',
			none: 'none',
		},
		fontFamily: {
			sans: '"Open Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, "Noto Sans", sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"',
		},
		extend: {
			width: {
				18: '4.5rem',
				72: '18rem',
				80: '20rem',
				84: '21rem',
				96: '24rem',
				99: '72rem',
				'3/10': '30%',
				'7/10': '70%',
				'9/10': '90%',
				'6/13': '46.15384615384615%',
				'8/17': '47.059%',
				'2/9': '22.2222222%',
			},
			minWidth: {
				10: '1rem',
				18: '4.5rem',
			},
			height: {
				28: '7rem',
				72: '18rem',
				80: '20rem',
			},
			fontSize: {
				h1: '2rem',
				h2: '1.75rem',
				h3: '1.5rem',
				subhead: '1rem',
			},
			maxWidth: {
				18: '4.5rem',
				'7xl': '80rem',
				'8xl': '88rem',
				'150px': '150px',
				'200px': '200px',
				quoteSlider: '400px',
				'1/2': '50%',
				'1/3': '33.3333333%',
				'3/20': '15%',
				none: 'none',
			},
			maxHeight: {
				'3/4': '75vh',
				none: 'none',
			},
			minHeight: {
				450: '450px',
			},
			borderRadius: {
				sm: '3px',
			},
			borderWidth: {
				3: '3px',
				6: '6px',
				16: '16px',
			},
			zIndex: {
				100: '100',
			},
			screens: {
				xxs: {min: '0px', max: '428px'},
				xs: {min: '480px', max: '640px'},
				slg: {min: '1024px', max: '1419px'},
				xlg: {min: '1420px', max: '1536px'},
			},
			backgroundColor: {
				main: 'var(--color-bg-main)',
				secondary: 'var(--color-bg-secondary)',
				highlight: 'var(--color-bg-highlight)',
			},
			textColor: {
				accent: 'var(--color-text-accent)',
				main: 'var(--color-text-primary)',
				alternative: 'var(--color-text-secondary)',
				subtheme: 'var(--color-theme)',
			},
			opacity: {
				10: '0.1',
			},
			boxShadow: {
				sm: '0px 3px 6px #00000029',
			},
			inset: {
				'-88': '-21.875rem!important',
				35: '9.8rem!important',
			},
			colors: {
				inverted_zenrelia_primary: '#215447',
			},
		},
	},
	corePlugins: {
		container: false,
	},
}
